import { useMutation } from "@tanstack/react-query";
import type { User as FirebaseUser } from "firebase/auth";
import {
	signOut as firebaseSignOut,
	getRedirectResult,
	onAuthStateChanged,
	signInWithPopup,
	signInWithRedirect,
} from "firebase/auth";
import { toast } from "sonner";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";
import authService from "@/api/services/authService";
import userService, { mapUserMeResponseToUserInfo, type SignInReq } from "@/api/services/userService";
import { auth, googleProvider } from "@/lib/firebase";

const PERMISSION_DENIED_MSG = "您的账户没有权限访问管理系统";

/**
 * 检查用户角色是否为 "user"，如果是则拒绝登录
 */
const checkUserRole = (userInfo: UserInfo): boolean => {
	const userRole = userInfo.role || userInfo.roles?.[0];
	if (userRole === "user") {
		return false;
	}
	return true;
};

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	firebaseUser: FirebaseUser | null;
	loading: boolean;
	error: string | null;
	/** persist 恢复 + Firebase 首次回调完成后为 true，路由守卫应等待此标志 */
	authReady: boolean;

	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		clearUserInfoAndToken: () => void;
		signInWithGoogle: () => Promise<void>;
		initAuth: () => void | Promise<void>;
		signOut: () => Promise<void>;
		clearError: () => void;
	};
};

type StoreGet = () => UserStore;
type StoreSet = (
	partial: Partial<UserStore> | ((state: UserStore) => Partial<UserStore>),
) => void;

let authUnsubscribe: (() => void) | null = null;
let waitForHydration: () => Promise<void> = () => Promise.resolve();

async function rejectUnauthorizedUser(get: StoreGet, set: StoreSet) {
	await firebaseSignOut(auth);
	get().actions.clearUserInfoAndToken();
	set({
		error: `${PERMISSION_DENIED_MSG}，请联系管理员`,
		loading: false,
	});
	toast.error(PERMISSION_DENIED_MSG, { position: "top-center" });
	window.location.replace("/auth/login");
}

/** 用已有 JWT 向后端同步用户信息；失败则清除本地登录态 */
async function syncSessionFromBackend(get: StoreGet, set: StoreSet): Promise<boolean> {
	if (!get().userToken.accessToken) {
		return false;
	}
	try {
		const userMe = await userService.getCurrentUser();
		const userInfo = mapUserMeResponseToUserInfo(userMe);
		if (!checkUserRole(userInfo)) {
			await rejectUnauthorizedUser(get, set);
			return false;
		}
		get().actions.setUserInfo(userInfo);
		return true;
	} catch (error) {
		console.error("Failed to sync session from backend:", error);
		get().actions.clearUserInfoAndToken();
		return false;
	}
}

async function handleFirebaseSignedIn(user: FirebaseUser, get: StoreGet, set: StoreSet) {
	try {
		const idToken = await user.getIdToken();
		set({ firebaseUser: user });

		if (!get().userToken.accessToken) {
			const res = await authService.verifyFirebaseToken(idToken);
			if (res.token) {
				get().actions.setUserToken({ accessToken: res.token });
				await syncSessionFromBackend(get, set);
			}
		} else if (!get().userInfo.id) {
			await syncSessionFromBackend(get, set);
		}
	} catch (error) {
		console.error("Failed to handle Firebase signed-in user:", error);
		set({ firebaseUser: user });
	}
}

const useUserStore = create<UserStore>()(
	persist(
		(set, get) => ({
			userInfo: {},
			userToken: {},
			firebaseUser: null,
			loading: false,
			error: null,
			authReady: false,
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					set({ userToken });
				},
				clearUserInfoAndToken() {
					set({ userInfo: {}, userToken: {}, firebaseUser: null });
				},
				clearError: () => {
					set({ error: null });
				},
				initAuth: async () => {
					if (authUnsubscribe) {
						return;
					}

					set({ loading: true });

					await waitForHydration();

					if (import.meta.env.PROD) {
						try {
							await getRedirectResult(auth);
						} catch (error: any) {
							console.error("Redirect result error:", error);
							set({
								error: error?.message || "重定向登录失败",
								loading: false,
							});
							toast.error(error?.message || "重定向登录失败", {
								position: "top-center",
							});
						}
					}

					let bootstrapped = false;

					authUnsubscribe = onAuthStateChanged(auth, (user) => {
						void (async () => {
							if (user) {
								await handleFirebaseSignedIn(user, get, set);
							} else {
								// ponytail: Firebase 会话缺失不等于后端 JWT 失效，勿在此清除 token
								set({ firebaseUser: null });
							}

							if (!bootstrapped) {
								bootstrapped = true;
								if (!user && get().userToken.accessToken) {
									await syncSessionFromBackend(get, set);
								}
								set({ authReady: true, loading: false });
							}
						})();
					});
				},
				signInWithGoogle: async () => {
					set({ loading: true, error: null });
					try {
						if (auth.currentUser) {
							await firebaseSignOut(auth);
						}
						const isDev = import.meta.env.DEV;
						if (isDev) {
							const result = await signInWithPopup(auth, googleProvider);
							const idToken = await result.user.getIdToken();
							set({ firebaseUser: result.user });

							const res = await authService.verifyFirebaseToken(idToken);
							if (!res.token) {
								throw new Error(res.message || "后端验证失败");
							}
							get().actions.setUserToken({ accessToken: res.token });

							const userMe = await userService.getCurrentUser();
							const userInfo = mapUserMeResponseToUserInfo(userMe);
							if (!checkUserRole(userInfo)) {
								await rejectUnauthorizedUser(get, set);
								throw new Error("用户角色没有权限");
							}
							get().actions.setUserInfo(userInfo);
							set({ loading: false });
							toast.success("登录成功", { position: "top-center" });
						} else {
							await signInWithRedirect(auth, googleProvider);
						}
					} catch (error: any) {
						if (error.message === "用户角色没有权限") {
							throw error;
						}
						console.error("Google sign in error:", error);
						const errorMessage = error.message || "登录失败，请重试";
						set({
							error: errorMessage,
							loading: false,
						});
						toast.error(errorMessage, {
							position: "top-center",
						});
						throw error;
					}
				},
				signOut: async () => {
					set({ loading: true, error: null });
					try {
						get().actions.clearUserInfoAndToken();
						await firebaseSignOut(auth);
						set({ loading: false });
					} catch (error: any) {
						console.error("Sign out error:", error);
						set({
							error: error.message || "登出失败，请重试",
							loading: false,
						});
						toast.error(error.message || "登出失败", {
							position: "top-center",
						});
						throw error;
					}
				},
			},
		}),
		{
			name: "userStore",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
			}),
		},
	),
);

waitForHydration = () =>
	new Promise((resolve) => {
		if (useUserStore.persist.hasHydrated()) {
			resolve(undefined);
			return;
		}
		const unsub = useUserStore.persist.onFinishHydration(() => {
			unsub();
			resolve(undefined);
		});
	});

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useAuthReady = () => useUserStore((state) => state.authReady);
export const useUserPermissions = () => useUserStore((state) => state.userInfo.permissions || []);
export const useUserRoles = () => useUserStore((state) => state.userInfo.roles || []);
export const useUserActions = () => useUserStore((state) => state.actions);
export { useUserStore };

export const useSignIn = () => {
	const { setUserToken, setUserInfo } = useUserActions();

	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			const { user, accessToken, refreshToken } = res;
			if (!checkUserRole(user)) {
				toast.error(`${PERMISSION_DENIED_MSG}，请联系管理员`, {
					position: "top-center",
				});
				window.location.replace("/auth/login");
				throw new Error("用户角色没有权限");
			}
			setUserToken({ accessToken, refreshToken });
			setUserInfo(user);
		} catch (err) {
			toast.error(err.message, {
				position: "top-center",
			});
			throw err;
		}
	};

	return signIn;
};

export default useUserStore;
